import {
  PRICING_TEST_V1_ARMS,
  type PricingTestV1Variant,
} from '@listening/api-contract/pricing-experiment';
import {
  trialDays as defaultTrialDays,
  getDefaultPaymentPlans,
  getPricingTestPaymentPlans,
} from '@listening/frontend/pricing';
import { createContext, use } from 'react';
import { clientEnv } from '@/env';

// Even though we A/B test this in the web app, the marketing site is SSR
// We do not know the user's A/B bucket during SSR so we have to pick one.
export const isFreeTrialAvailable = false as boolean;

/**
 * The arm an anonymous surface renders when no arm was resolved for it —
 * a crawler, a cookie-less request, or a page the experiment is not
 * registered on.
 *
 * This is decision C4's fallback and D27's anchor, which are the same number
 * reached two ways: D27 pinned the top-priced arm as a constant so no
 * enrolled buyer could be quoted more at checkout than the page advertised,
 * and C4 generalizes that into "an unbucketable viewer sees the top arm".
 * Since the $79 arms were retired the top LIVE arm is $59, and that is what
 * this anchors to. Keyed into the arm contract rather than written as a
 * number, so deleting or renaming the arm stops this compiling;
 * `pricing-page-anchor.spec.tsx` pins it to both the TOP live tier and the
 * edge definition's own `fallbackVariant`, so the three cannot drift apart
 * silently.
 */
export const MARKETING_FALLBACK_ARM =
  'price-59-no-trial' satisfies PricingTestV1Variant;

// Adapter to map new config property names to what the shared library expects
const envAdapter = {
  VITE_STRIPE_MONTHLY_12_99_ID: clientEnv.stripeMonthly1299Id,
  VITE_STRIPE_YEARLY_99_ID: clientEnv.stripeYearly99Id,
  VITE_STRIPE_YEARLY_39_ID: clientEnv.stripeYearly39Id,
  VITE_STRIPE_YEARLY_59_ID: clientEnv.stripeYearly59Id,
  VITE_STRIPE_YEARLY_79_ID: clientEnv.stripeYearly79Id,
};

export const defaultPaymentPlans = getDefaultPaymentPlans(envAdapter);

/** The figures an anonymous marketing surface can put on the page. */
export type MarketingPricing = {
  readonly yearlyUsd: number;
  /** Derived to the cent ($59/12 → $4.92): the "per month (paid yearly)" figure. */
  readonly yearlyPerMonthUsd: number;
  /** $12.99 in every arm — read from the catalog, not restated. */
  readonly monthlyUsd: number;
  /** The arm's nominal trial, before checkout checks eligibility. */
  readonly trialDays: number;
};

/**
 * The prices one anonymous surface renders, for one resolved arm.
 *
 * Replaces the D27 constant this used to be. The constant was right while
 * `/pricing` could not know the visitor's arm; now that the edge assigns it
 * before the page is cached, the honest display is the visitor's OWN arm and
 * the anchor becomes what an unresolved viewer sees — the top live arm, derived
 * rather than declared. Pass the arm where one was resolved, nothing where one
 * was not; both cases are real and neither is an error.
 *
 * Display-only, still. Checkout quotes come from the server-validated arm, and
 * `defaultPaymentPlans` above stays the checkout/plan-picker fallback catalog
 * at control ($39) — an arm rendered here must never reach a charge.
 */
export function marketingPricingForArm(
  arm?: PricingTestV1Variant | null,
): MarketingPricing {
  const variant = arm ?? MARKETING_FALLBACK_ARM;
  const { trialDays } = PRICING_TEST_V1_ARMS[variant];
  const { yearly, monthly } = getPricingTestPaymentPlans(envAdapter, variant);
  return {
    yearlyUsd: yearly.amount,
    yearlyPerMonthUsd: Math.round((yearly.amount / 12) * 100) / 100,
    monthlyUsd: monthly.amount,
    trialDays,
  };
}

const TrialDaysContext = createContext(defaultTrialDays);

export function useTrialDays() {
  return use(TrialDaysContext);
}
